"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Toolbar from "./Toolbar";
import Preview from "./Preview";
import ThemePicker from "./ThemePicker";
import ColorPresetPicker from "./ColorPresetPicker";
import { getColorPreset } from "@/lib/themes";

const Editor = dynamic(() => import("./Editor"), { ssr: false });

interface EditorPageProps {
  initialContent?: string;
  initialTitle?: string;
  initialTheme?: string;
  initialColorPreset?: string;
  secretId?: string;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export default function EditorPage({
  initialContent = "",
  initialTitle = "",
  initialTheme = "paper",
  initialColorPreset = "copper",
  secretId: initialSecretId,
}: EditorPageProps) {
  const { data: session } = useSession();
  const [content, setContent] = useState(initialContent);
  const [title, setTitle] = useState(initialTitle);
  const [theme, setTheme] = useState(initialTheme);
  const [colorPreset, setColorPreset] = useState(initialColorPreset);
  const [secretId, setSecretId] = useState(initialSecretId);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [customSlug, setCustomSlug] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [pagePassword, setPagePassword] = useState("");
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [maxContentLength, setMaxContentLength] = useState<number | undefined>(undefined);
  const lastSavedRef = useRef<{ content: string; title: string; theme: string; colorPreset: string }>({
    content: initialContent,
    title: initialTitle,
    theme: initialTheme,
    colorPreset: initialColorPreset,
  });

  // Auto-save every 30 seconds when a secretId exists
  useEffect(() => {
    if (!secretId) return;

    const interval = setInterval(async () => {
      const hasChanges =
        content !== lastSavedRef.current.content ||
        title !== lastSavedRef.current.title ||
        theme !== lastSavedRef.current.theme ||
        colorPreset !== lastSavedRef.current.colorPreset;

      if (!hasChanges) return;

      setSaveStatus("saving");
      try {
        const res = await fetch(`/api/documents/${secretId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content, title, theme, colorPreset }),
        });
        if (res.ok) {
          lastSavedRef.current = { content, title, theme, colorPreset };
          setSaveStatus("saved");
        } else {
          setSaveStatus("error");
        }
      } catch {
        setSaveStatus("error");
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [secretId, content, title, theme, colorPreset]);

  // Fetch user plan to determine editor character limit
  useEffect(() => {
    if (!session?.user) {
      setMaxContentLength(100_000);
      return;
    }
    fetch("/api/me/plan")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.plan === "pro") {
          setMaxContentLength(undefined); // unlimited
        } else {
          setMaxContentLength(100_000);
        }
      })
      .catch(() => setMaxContentLength(100_000));
  }, [session?.user]);

  const handleInsert = useCallback((before: string, after?: string) => {
    setContent((prev) => prev + before + (after || ""));
  }, []);

  const handleSaveNow = useCallback(async () => {
    if (!secretId) return;
    setSaveStatus("saving");
    try {
      const res = await fetch(`/api/documents/${secretId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, title, theme, colorPreset }),
      });
      if (res.ok) {
        lastSavedRef.current = { content, title, theme, colorPreset };
        setSaveStatus("saved");
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    }
  }, [secretId, content, title, theme, colorPreset]);

  const handlePublish = useCallback(async () => {
    if (!content || content.trim() === "") {
      setPublishError("Cannot publish empty content. Write something first!");
      setTimeout(() => setPublishError(null), 4000);
      return;
    }
    setPublishError(null);

    let currentSecretId = secretId;

    if (!currentSecretId) {
      try {
        const createRes = await fetch("/api/documents", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title }),
        });
        if (!createRes.ok) {
          setSaveStatus("error");
          return;
        }
        const createData = await createRes.json();
        currentSecretId = createData.secretId as string;
        setSecretId(currentSecretId);
        window.history.replaceState(null, "", `/edit/${currentSecretId}`);

        const saveRes = await fetch(`/api/documents/${currentSecretId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content, title, theme, colorPreset }),
        });
        if (!saveRes.ok) {
          setSaveStatus("error");
          return;
        }
        lastSavedRef.current = { content, title, theme, colorPreset };
      } catch {
        setSaveStatus("error");
        return;
      }
    } else {
      await handleSaveNow();
    }

    try {
      const publishRes = await fetch(`/api/documents/${currentSecretId}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (publishRes.ok) {
        const publishData = await publishRes.json();
        setPublishedSlug(publishData.slug);
        setCustomSlug(publishData.slug);
        setPublishDialogOpen(true);
      } else {
        const errData = await publishRes.json().catch(() => ({}));
        if (publishRes.status === 403 && errData.error) {
          setPublishError(errData.error);
        } else {
          setSaveStatus("error");
        }
      }
    } catch {
      setSaveStatus("error");
    }
  }, [secretId, content, title, theme, colorPreset, handleSaveNow]);

  const publicUrl =
    publishedSlug && typeof window !== "undefined"
      ? `${window.location.origin}/p/${publishedSlug}`
      : "";

  const handleCopy = useCallback(() => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [publicUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === "s") {
        e.preventDefault();
        handleSaveNow();
      }

      if (isMod && e.key === "Enter") {
        e.preventDefault();
        handlePublish();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSaveNow, handlePublish]);

  const handleSaveSlug = useCallback(async () => {
    if (!secretId || !customSlug) return;
    setSlugSaving(true);
    setSlugError(null);
    try {
      const res = await fetch(`/api/documents/${secretId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: customSlug }),
      });
      if (res.ok) {
        setPublishedSlug(customSlug);
      } else {
        const data = await res.json();
        setSlugError(data.error || "Failed to update slug");
      }
    } catch {
      setSlugError("Failed to update slug");
    } finally {
      setSlugSaving(false);
    }
  }, [secretId, customSlug]);

  const hasUnsavedChanges = secretId && (
    content !== lastSavedRef.current.content ||
    title !== lastSavedRef.current.title ||
    theme !== lastSavedRef.current.theme
  );

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-border relative z-10">
        {/* Logo */}
        <div className="flex items-center gap-4 shrink-0">
          <Link href="/" className="font-mono font-bold text-foreground no-underline">
            Pub<span className="text-foreground">mark</span>
          </Link>

          {session?.user && (
            <Link
              href="/dashboard"
              className="text-sm text-muted-foreground hover:underline no-underline"
            >
              Dashboard
            </Link>
          )}
        </div>

        {/* Title input */}
        <div className="flex-1 min-w-0 flex justify-center">
          <input
            type="text"
            placeholder="Untitled"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="bg-transparent text-sm font-medium focus:outline-none text-foreground w-full max-w-[400px] text-center"
          />
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-4 shrink-0">
          <div className="hide-mobile">
            <ThemePicker value={theme} onChange={setTheme} />
          </div>

          {/* Separator */}
          <div className="hide-mobile w-px h-4 bg-border" />

          <div className="hide-mobile">
            <ColorPresetPicker value={colorPreset} onChange={setColorPreset} />
          </div>

          {/* Separator */}
          <div className="hide-mobile w-px h-4 bg-border" />

          {/* Save indicator */}
          {secretId && (
            <div className="flex items-center gap-1.5">
              {saveStatus === "saving" ? (
                <>
                  <div
                    className="w-3 h-3 border-2 border-foreground border-t-transparent rounded-full"
                    style={{ animation: "spin 0.8s linear infinite" }}
                  />
                  <span className="text-xs text-muted-foreground font-mono">
                    Saving...
                  </span>
                </>
              ) : saveStatus === "saved" && !hasUnsavedChanges ? (
                <>
                  <div
                    className="w-2 h-2 rounded-full bg-green-500"
                    style={{ animation: "dotPop 0.3s ease-out" }}
                  />
                  <span className="text-xs text-muted-foreground font-mono">
                    Saved
                  </span>
                </>
              ) : saveStatus === "error" ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-destructive" />
                  <span className="text-xs text-destructive font-mono">
                    Error
                  </span>
                </>
              ) : hasUnsavedChanges ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-mono">
                    Unsaved
                  </span>
                </>
              ) : null}
            </div>
          )}

          {secretId && (
            <button
              onClick={handleSaveNow}
              title="Save (Cmd+S)"
              className="border border-border rounded-lg px-4 py-2 text-sm hover:bg-muted transition-colors"
            >
              Save
            </button>
          )}

          <button
            onClick={handlePublish}
            title="Publish (Cmd+Enter)"
            className="bg-foreground text-background rounded-lg px-4 py-2 text-sm font-medium hover:bg-foreground/90 transition-colors"
          >
            Publish
          </button>
        </div>
      </div>

      {/* Publish error notification */}
      {publishError && (
        <div className="bg-destructive/10 border-b border-destructive px-4 py-2.5 flex items-center gap-2.5">
          <span className="text-destructive text-sm">&#9888;</span>
          <span className="text-sm font-medium text-destructive flex-1">
            {publishError}
            {publishError.includes("Upgrade to Pro") && (
              <Link
                href="/dashboard/upgrade"
                className="text-foreground font-semibold ml-1.5 underline"
              >
                Upgrade to Pro
              </Link>
            )}
          </span>
          <button
            onClick={() => setPublishError(null)}
            className="bg-transparent border-none text-destructive cursor-pointer text-lg leading-none px-0.5"
          >
            &times;
          </button>
        </div>
      )}

      {/* Anonymous user banner */}
      {!session?.user && !bannerDismissed && (
        <div className="bg-muted border-b border-border px-4 py-2 text-sm text-muted-foreground flex items-center justify-between">
          <span>
            Sign up to save and manage your documents.{" "}
            <Link
              href="/register"
              className="text-foreground underline font-semibold"
            >
              Create an account
            </Link>
          </span>
          <button
            onClick={() => setBannerDismissed(true)}
            className="bg-transparent border-none text-muted-foreground cursor-pointer text-xl leading-none px-1"
          >
            &times;
          </button>
        </div>
      )}

      {/* Publish success panel */}
      {publishDialogOpen && publicUrl && (
        <div className="bg-background border-b border-border" style={{ animation: "slideDown 0.25s ease-out" }}>
          <div className="px-4 py-2.5 flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center text-xs shrink-0">
              &#10003;
            </div>
            <span className="text-sm font-medium text-foreground">
              Published!
            </span>
            <span className="font-mono text-sm text-foreground overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0">
              {passwordEnabled && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline align-middle mr-1">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              )}
              {publicUrl}
            </span>
            <button
              onClick={handleCopy}
              className="border border-border rounded-lg px-3 py-1 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors shrink-0 hover:bg-muted"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-foreground underline shrink-0 hover:text-foreground/80"
            >
              Open
            </a>
            <button
              onClick={() => setPublishDialogOpen(false)}
              className="bg-transparent border-none text-muted-foreground cursor-pointer text-lg leading-none px-0.5 shrink-0"
            >
              &times;
            </button>
          </div>
          {/* Custom slug editor */}
          <div className="px-4 py-1.5 pb-2.5 flex items-center gap-2 border-t border-border">
            <span className="text-xs font-medium text-muted-foreground shrink-0">
              Custom URL:
            </span>
            <input
              type="text"
              value={customSlug}
              onChange={(e) => {
                const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                setCustomSlug(val);
                setSlugError(null);
              }}
              placeholder="my-custom-slug"
              className={`flex-1 max-w-[280px] rounded-lg border bg-background px-3 py-2 text-sm font-mono focus:border-foreground focus:outline-none transition-colors ${slugError ? "border-destructive" : "border-border"}`}
            />
            <button
              onClick={handleSaveSlug}
              disabled={slugSaving || customSlug === publishedSlug}
              className={`rounded-lg px-3 py-1 text-xs font-medium shrink-0 transition-colors ${
                customSlug === publishedSlug
                  ? "bg-transparent border border-border text-muted-foreground cursor-default"
                  : "bg-foreground text-background cursor-pointer hover:bg-foreground/90"
              }`}
              style={{ opacity: slugSaving ? 0.6 : 1 }}
            >
              {slugSaving ? "Saving..." : "Save"}
            </button>
            {slugError && (
              <span className="text-xs text-destructive shrink-0">
                {slugError}
              </span>
            )}
          </div>
          {/* Password protection row */}
          <div className="px-4 py-1.5 pb-2.5 flex items-center gap-2 border-t border-border">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground shrink-0">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="text-xs font-medium text-muted-foreground shrink-0">
              Password protect:
            </span>
            {/* Toggle switch */}
            <button
              onClick={() => {
                const next = !passwordEnabled;
                setPasswordEnabled(next);
                if (!next && secretId) {
                  // Remove password
                  fetch(`/api/documents/${secretId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ pagePassword: null }),
                  });
                  setPasswordSaved(false);
                  setPagePassword("");
                }
              }}
              className="shrink-0 relative border-none cursor-pointer rounded-full transition-colors"
              style={{
                width: "36px",
                height: "20px",
                background: passwordEnabled ? "var(--foreground)" : "var(--muted)",
              }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: passwordEnabled ? "18px" : "2px" }}
              />
            </button>
            {passwordEnabled && !passwordSaved && (
              <>
                <input
                  type="password"
                  value={pagePassword}
                  onChange={(e) => setPagePassword(e.target.value)}
                  placeholder="Enter password"
                  className="flex-1 max-w-[200px] rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono focus:border-foreground focus:outline-none transition-colors"
                />
                <button
                  onClick={async () => {
                    if (!secretId || !pagePassword) return;
                    setPasswordSaving(true);
                    try {
                      const res = await fetch(`/api/documents/${secretId}`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ pagePassword }),
                      });
                      if (res.ok) {
                        setPasswordSaved(true);
                        setTimeout(() => setPasswordSaved(false), 3000);
                      }
                    } finally {
                      setPasswordSaving(false);
                    }
                  }}
                  disabled={passwordSaving || !pagePassword}
                  className={`rounded-lg px-3 py-1 text-xs font-medium shrink-0 transition-colors ${
                    !pagePassword
                      ? "bg-transparent border border-border text-muted-foreground cursor-default"
                      : "bg-foreground text-background cursor-pointer hover:bg-foreground/90"
                  }`}
                  style={{ opacity: passwordSaving ? 0.6 : 1 }}
                >
                  {passwordSaving ? "Saving..." : "Set Password"}
                </button>
              </>
            )}
            {passwordEnabled && passwordSaved && (
              <span className="text-xs font-medium text-green-500" style={{ animation: "fadeIn 0.3s ease-out" }}>
                Protected
              </span>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <Toolbar onInsert={handleInsert} />

      {/* Mobile tab toggle */}
      <div className="flex md:hidden border-b border-border bg-background">
        <button
          onClick={() => setActiveTab("edit")}
          className={`flex-1 text-sm px-3 py-1 rounded-lg transition-colors ${
            activeTab === "edit" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted"
          }`}
        >
          Edit
        </button>
        <button
          onClick={() => setActiveTab("preview")}
          className={`flex-1 text-sm px-3 py-1 rounded-lg transition-colors ${
            activeTab === "preview" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted"
          }`}
        >
          Preview
        </button>
      </div>

      {/* Editor and Preview panes */}
      <div className="flex flex-1 overflow-hidden">
        <div
          className={`flex-1 overflow-auto relative ${
            activeTab === "edit" ? "block" : "hidden"
          } md:block`}
        >
          {content === "" && (
            <div className="editor-empty-state">
              <span>Start writing in markdown...</span>
            </div>
          )}
          <Editor value={content} onChange={setContent} className="h-full" maxLength={maxContentLength} />
          {maxContentLength !== undefined && (
            <div
              className={`absolute bottom-2 right-4 text-xs font-mono pointer-events-none z-10 ${
                content.length >= maxContentLength * 0.9 ? "text-destructive" : "text-muted-foreground"
              }`}
            >
              {content.length.toLocaleString()} / {maxContentLength.toLocaleString()}
            </div>
          )}
        </div>
        <div className="w-px bg-border hidden md:block" />
        <div
          className={`flex-1 overflow-auto p-6 relative ${
            activeTab === "preview" ? "block" : "hidden"
          } md:block`}
        >
          <Preview content={content} theme={theme} colorPreset={colorPreset} className="h-full" />
        </div>
      </div>

      {/* Publish slide-down panel */}

    </div>
  );
}
